<template>
    <div class="flex flex-wrap items-center gap-2">
        <date-range-selector v-model="dateRange"/>

        <transaction-filter-account
            v-if="!isRangeMode"
            :account-no="model.accountNo"
            @on-account-no-selected="onAccountNoSelected"
        />
        <div v-else class="flex items-center gap-1 h-8 px-2 rounded border border-neutral-300 text-sm">
            <span class="text-neutral-500 text-xs">Konto</span>
            <span class="font-semibold">{{ model.fromAccountNo }} – {{ model.toAccountNo }}</span>
            <UButton
                icon="i-ph-x"
                size="xs"
                color="neutral"
                variant="ghost"
                @click="clearAccountRange"
            />
        </div>

        <UInput
            v-model="searchText"
            icon="i-ph-magnifying-glass"
            placeholder="Søk i bilag"
            class="grow max-w-[300px]"
            autocomplete="off"
            @keyup="debouncedApplySearch"
        >
            <template #trailing>
                <UButton
                    v-if="searchText"
                    icon="i-ph-x"
                    size="sm"
                    color="neutral"
                    variant="ghost"
                    @click="clearSearch"
                />
                <div v-else/>
            </template>
        </UInput>

        <department-picker-new v-model="department" size="md"/>
        <project-picker-new v-model="project" size="md"/>
    </div>
</template>

<script setup lang="ts">
import {computed, ref, watch} from 'vue'
import {debounce, omit} from 'lodash'
import {format} from 'date-fns'
import type {GenLegQuery} from '@/genleg/GenLegQuery'
import DateRangeSelector from '@/dateUtils/DateRangeSelector.vue'
import TransactionFilterAccount from '@/transaction/TransactionFilterAccount.vue'
import DepartmentPickerNew from '@/dimensions/DepartmentPickerNew.vue'
import ProjectPickerNew from '@/dimensions/ProjectPickerNew.vue'

const model = defineModel<GenLegQuery>({required: true})

// ── Date range ────────────────────────────────────────────────────────────────

const dateRange = computed({
    get: () => ({start: new Date(model.value.fromDate), end: new Date(model.value.toDate)}),
    set: (range) => {
        const newFrom = format(range.start, 'yyyy-MM-dd')
        const newTo = format(range.end, 'yyyy-MM-dd')
        if (newFrom === model.value.fromDate && newTo === model.value.toDate) return
        model.value = {...model.value, fromDate: newFrom, toDate: newTo}
    },
})

// ── Account filter ────────────────────────────────────────────────────────────

const isRangeMode = computed(() => !!model.value.fromAccountNo && !!model.value.toAccountNo)

const onAccountNoSelected = (no: string | undefined) => {
    const base = omit(model.value, 'accountNo', 'fromAccountNo', 'toAccountNo')
    model.value = no ? {...base, accountNo: no} : base
}

const clearAccountRange = () => {
    model.value = omit(model.value, 'accountNo', 'fromAccountNo', 'toAccountNo')
}

// ── Full-text search ──────────────────────────────────────────────────────────

const searchText = ref(model.value.fullText ?? '')

watch(() => model.value.fullText, (incoming) => {
    const val = incoming ?? ''
    if (val !== searchText.value) searchText.value = val
})

const applySearch = () => {
    model.value = searchText.value
        ? {...model.value, fullText: searchText.value}
        : omit(model.value, 'fullText')
}

const debouncedApplySearch = debounce(applySearch, 500)

const clearSearch = () => {
    searchText.value = ''
    applySearch()
}

// ── Department ────────────────────────────────────────────────────────────────

const department = computed<string>({
    get: () => model.value.department ?? '',
    set: (value) => {
        model.value = value ? {...model.value, department: value} : omit(model.value, 'department')
    },
})

// ── Project ───────────────────────────────────────────────────────────────────

const project = computed<string>({
    get: () => model.value.project ?? '',
    set: (value) => {
        model.value = value ? {...model.value, project: value} : omit(model.value, 'project')
    },
})
</script>
